import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';
import { Search, Car, LayoutGrid, Tag, ChevronDown } from 'lucide-react';
import { formatPrice } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import type { Tables } from '@/integrations/supabase/types';

type Brand = Tables<'brands'>;
type Model = Tables<'models'>;

const PRICE_STEPS = [200000, 300000, 400000, 500000, 600000, 800000, 1000000];

type FieldRowProps = {
  icon: React.ReactNode;
  label: string;
  value: string;
  hasValue: boolean;
  disabled?: boolean;
};

function FieldRow({ icon, label, value, hasValue, disabled }: FieldRowProps) {
  return (
    <div
      className={cn(
        'flex items-center gap-3 w-full text-left',
        disabled && 'opacity-60'
      )}
    >
      <div
        className={cn(
          'flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center',
          hasValue
            ? 'bg-primary text-primary-foreground'
            : 'bg-muted text-muted-foreground'
        )}
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0 flex flex-col justify-center">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground leading-none">
          {label}
        </span>
        <span
          className={cn(
            'text-sm leading-tight truncate mt-1',
            hasValue ? 'text-foreground font-semibold' : 'text-muted-foreground'
          )}
        >
          {value}
        </span>
      </div>
      <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
    </div>
  );
}

export function QuickCarSearchBar() {
  const navigate = useNavigate();
  const [brandId, setBrandId] = useState<string>('');
  const [modelId, setModelId] = useState<string>('');
  const [maxPrice, setMaxPrice] = useState<string>('');

  const { data: brands = [] } = useQuery({
    queryKey: ['brands-quicksearch'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('brands')
        .select('id, name, slug')
        .order('name');
      if (error) throw error;
      return data as Pick<Brand, 'id' | 'name' | 'slug'>[];
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: models = [] } = useQuery({
    queryKey: ['models-quicksearch', brandId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('models')
        .select('id, name, slug, brand_id')
        .eq('brand_id', brandId)
        .order('name');
      if (error) throw error;
      return data as Pick<Model, 'id' | 'name' | 'slug' | 'brand_id'>[];
    },
    enabled: !!brandId,
    staleTime: 5 * 60 * 1000,
  });

  const sortedBrands = useMemo(
    () => [...brands].sort((a, b) => a.name.localeCompare(b.name, 'da')),
    [brands]
  );

  const selectedBrand = sortedBrands.find((b) => b.id === brandId);
  const selectedModel = models.find((m) => m.id === modelId);

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (brandId) params.set('brandId', brandId);
    if (modelId) params.set('modelId', modelId);
    if (maxPrice) params.set('maxPrice', maxPrice);
    const q = params.toString();
    navigate(q ? `/biler?${q}` : '/biler');
  };

  const triggerBase =
    'h-auto min-h-[60px] w-full bg-background border border-border rounded-xl px-3 py-2.5 [&>svg]:hidden focus:ring-2 focus:ring-primary/30 hover:border-primary/40 transition-colors data-[state=open]:border-primary/50 data-[state=open]:ring-2 data-[state=open]:ring-primary/20';

  return (
    <div className="bg-card border border-border rounded-2xl shadow-xl p-3 md:p-3">
      <div className="flex flex-col md:flex-row gap-2 md:items-stretch">
        {/* Brand */}
        <div className="flex-1 min-w-0">
          <Select
            value={brandId}
            onValueChange={(v) => {
              setBrandId(v);
              setModelId('');
            }}
          >
            <SelectTrigger className={triggerBase} aria-label="Vælg mærke">
              <FieldRow
                icon={<Car className="w-4 h-4" />}
                label="Mærke"
                value={selectedBrand?.name ?? 'Alle mærker'}
                hasValue={!!selectedBrand}
              />
            </SelectTrigger>
            <SelectContent>
              {sortedBrands.map((b) => (
                <SelectItem key={b.id} value={b.id}>
                  {b.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Model */}
        <div className="flex-1 min-w-0">
          <Select value={modelId} onValueChange={setModelId} disabled={!brandId}>
            <SelectTrigger className={triggerBase} aria-label="Vælg model">
              <FieldRow
                icon={<LayoutGrid className="w-4 h-4" />}
                label="Model"
                value={
                  selectedModel?.name ??
                  (brandId ? 'Alle modeller' : 'Vælg mærke først')
                }
                hasValue={!!selectedModel}
                disabled={!brandId}
              />
            </SelectTrigger>
            <SelectContent>
              {models.map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  {m.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Max price */}
        <div className="flex-1 min-w-0">
          <Select value={maxPrice} onValueChange={setMaxPrice}>
            <SelectTrigger className={triggerBase} aria-label="Maks. pris">
              <FieldRow
                icon={<Tag className="w-4 h-4" />}
                label="Maks. pris"
                value={maxPrice ? `Op til ${formatPrice(Number(maxPrice))}` : 'Ingen grænse'}
                hasValue={!!maxPrice}
              />
            </SelectTrigger>
            <SelectContent>
              {PRICE_STEPS.map((p) => (
                <SelectItem key={p} value={p.toString()}>
                  Op til {formatPrice(p)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Search button */}
        <Button
          onClick={handleSearch}
          size="lg"
          className="h-[60px] md:h-auto md:w-auto w-full gap-2 rounded-xl font-semibold"
        >
          <Search className="w-4 h-4" />
          Søg biler
        </Button>
      </div>
    </div>
  );
}
